import type { B2bCustomer, B2bOrderStatus, B2bScheme, OrderLineInput, PaymentTermTemplate, ProductDetail, ProductDetailSetting, SchemeEntitlement } from '@api/b2b';
import type { FilterParam, FilterValue } from '@components/ui/EntityFilterDropdown';
import { formatCount, formatMoney } from '@utils/currency';

/* Formatting and helpers shared by the brand's B2B screens and the customer portal. */

export const money = (value: number | null | undefined): string => (value === null || value === undefined ? '—' : formatMoney(value));

export const units = (value: number | null | undefined): string => (value === null || value === undefined ? '—' : formatCount(value));

export const formatDay = (value: string | Date | null | undefined): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatMoment = (value: string | Date | null | undefined): string => {
    if (!value) return '—';
    return new Date(value).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/** Today in India as yyyy-MM-dd, the business date every B2B form uses. */
export const todayIst = (): string => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

/** The steps of a B2B order, in order, for the progress strip. */
export const ORDER_STEPS: B2bOrderStatus[] = ['Submitted', 'PI Issued', 'PI Confirmed', 'Payment Submitted', 'Payment Done', 'Shipped', 'Fulfilled'];

/** Opens a signed file link in a new tab; the link is fetched only when clicked. */
export const openSignedFile = async (fetchUrl: () => Promise<{ url: string }>): Promise<void> => {
    const tab = window.open('', '_blank');
    try {
        const { url } = await fetchUrl();
        if (tab) tab.location.href = url;
        else window.location.href = url;
    } catch (error) {
        tab?.close();
        throw error;
    }
};

export const errorMessage = (error: unknown): string => {
    if (error && typeof error === 'object') {
        const e = error as { message?: unknown; error?: { message?: unknown } };
        if (typeof e.message === 'string') return e.message;
        if (Array.isArray(e.message)) return e.message.join('; ');
        if (e.error && typeof e.error.message === 'string') return e.error.message;
    }
    return 'Something went wrong. Please try again.';
};

export const termsLabel = (terms: B2bCustomer['paymentTerms']): string =>
    terms.length === 0 ? '—' : terms.map((t) => `${t.percentage}% ${t.type.toLowerCase()}${t.offsetDays ? ` +${t.offsetDays}d` : ''}`).join(', ');

export const describeScheme = (s: Pick<B2bScheme, 'benefitType' | 'slabBasis' | 'rule'>): string =>
    s.rule.slabs
        .map((slab) => {
            const from = s.slabBasis === 'VALUE' ? `from ${money(slab.min)} of order value` : `from ${slab.min} unit${slab.min === 1 ? '' : 's'}`;
            if (s.benefitType === 'FREE_QTY') return `${from}: ${slab.get ?? '?'} free for every ${slab.buy ?? '?'} bought`;
            if (s.benefitType === 'PERCENT') return `${from}: ${slab.value ?? '?'}% off`;
            return `${from}: ${money(slab.value)} off`;
        })
        .join('; ');

/** B2B order statuses in which the brand may still change lines, prices and free pieces. */
export const EDITABLE_STATUSES = new Set(['Submitted', 'PI Issued', 'PI Confirmed', 'Payment Submitted']);

/** A list the editor falls back to, most specific first: e.g. the customer's segment, then the default. */
export interface PriceFallback {
    owner: string;
    label: string;
}

/** The lists a price list falls back to, most specific first. */
export const priceFallbacks = (owner: string, segment?: { key: string; label: string | null } | null): PriceFallback[] => {
    if (owner === 'DEFAULT') return [];
    const fallbacks: PriceFallback[] = owner.startsWith('CUSTOMER:') && segment ? [{ owner: `SEGMENT:${segment.key}`, label: `${segment.label ?? segment.key} list` }] : [];
    return [...fallbacks, { owner: 'DEFAULT', label: 'Default list' }];
};

/** Payment terms as two numbers: % paid in advance at order, and days of credit after dispatch for the rest. */
export const ADVANCE: PaymentTermTemplate[] = [{ label: 'Advance', type: 'ADVANCE', percentage: 100, anchor: 'ORDER_DATE' }];

export const termsPreset = (advancePct: number, creditDays: number): PaymentTermTemplate[] =>
    advancePct >= 100
        ? ADVANCE
        : [
              ...(advancePct > 0 ? [{ label: 'Advance', type: 'ADVANCE' as const, percentage: advancePct, anchor: 'ORDER_DATE' }] : []),
              { label: 'Credit', type: 'CREDIT' as const, percentage: 100 - advancePct, anchor: 'DISPATCH', offsetDays: creditDays },
          ];

export const advanceOf = (terms?: PaymentTermTemplate[]) => (terms ?? ADVANCE).filter((t) => t.type === 'ADVANCE').reduce((s, t) => s + t.percentage, 0);
export const creditDaysOf = (terms?: PaymentTermTemplate[]) => (terms ?? []).find((t) => t.type === 'CREDIT')?.offsetDays ?? 0;

/* Free pieces: which scheme a free product counts against, the pre-filled split, and progress. */

/** The scheme a free product counts against: the first earned one whose free pool holds it (as the server matches it). */
export const freeSchemeOf = (entitlements: SchemeEntitlement[], productId: string): SchemeEntitlement | undefined =>
    entitlements.find((e) => e.pool.type === 'ANY' || e.pool.productIds.includes(productId));

/** The products a scheme's free pieces may be taken as: its pool, or every product on the order for an open pool. */
export const freeCandidates = (entitlements: SchemeEntitlement[], entitlement: SchemeEntitlement, lines: OrderLineInput[]): string[] => {
    const ids = entitlement.pool.type === 'ANY' ? lines.map((l) => l.productId) : entitlement.pool.productIds;
    return ids.filter((id) => freeSchemeOf(entitlements, id)?.schemeCode === entitlement.schemeCode);
};

/** Which earned scheme each product may be taken free under, for the products it can be: the Free column's editable cells. */
export const freeEligibility = (entitlements: SchemeEntitlement[], lines: OrderLineInput[]): Map<string, SchemeEntitlement> =>
    new Map(entitlements.filter((e) => e.units > 0).flatMap((e) => freeCandidates(entitlements, e, lines).map((id) => [id, e] as const)));

/**
 * Free pieces pre-filled for each scheme, split across its products by how much of each was ordered
 * (largest remainder; a tie goes to the product ordered more). 30 M + 15 L + 5 XL earning 4 gives 3 M + 1 L.
 */
export const defaultFreePicks = (entitlements: SchemeEntitlement[], lines: OrderLineInput[]): OrderLineInput[] => {
    const ordered = new Map(lines.map((l) => [l.productId, l.quantity]));
    return entitlements.flatMap((e) => {
        const ids = freeCandidates(entitlements, e, lines);
        if (e.units <= 0 || ids.length === 0) return [];
        const weights = ids.map((id) => ordered.get(id) ?? 0);
        const total = weights.reduce((s, w) => s + w, 0);
        if (total === 0) return [{ productId: ids[0], quantity: e.units }];
        // Whole numbers throughout, so equal remainders tie exactly.
        const shares = ids.map((id, i) => ({ id, i, rest: (e.units * weights[i]) % total }));
        const counts = ids.map((_, i) => Math.floor((e.units * weights[i]) / total));
        let left = e.units - counts.reduce((s, c) => s + c, 0);
        for (const s of [...shares].sort((a, b) => b.rest - a.rest || weights[b.i] - weights[a.i])) {
            if (left-- <= 0) break;
            counts[s.i] += 1;
        }
        return shares.filter((s) => counts[s.i] > 0).map((s) => ({ productId: s.id, quantity: counts[s.i] }));
    });
};

/** Free pieces chosen per scheme code, from the picks on screen. */
export const freeChosen = (entitlements: SchemeEntitlement[], picks: OrderLineInput[]): Map<string, number> => {
    const chosen = new Map<string, number>();
    for (const p of picks) {
        const code = freeSchemeOf(entitlements, p.productId)?.schemeCode;
        if (code && p.quantity > 0) chosen.set(code, (chosen.get(code) ?? 0) + p.quantity);
    }
    return chosen;
};

/** True when every pick belongs to an earned scheme and each scheme has exactly what it earned. */
export const freePicksComplete = (entitlements: SchemeEntitlement[], picks: OrderLineInput[]): boolean => {
    const chosen = freeChosen(entitlements, picks);
    return picks.every((p) => p.quantity <= 0 || !!freeSchemeOf(entitlements, p.productId)) && entitlements.every((e) => (chosen.get(e.schemeCode) ?? 0) === e.units);
};

/**
 * Where an order stands against a units-based free-goods scheme, from the quantities typed in:
 * qualifying units, free pieces earned, and how many more units earn the next free piece.
 * Null for schemes measured in rupees or giving a discount.
 */
export const freeSchemeProgress = (
    scheme: Pick<B2bScheme, 'benefitType' | 'slabBasis' | 'rule'>,
    lines: Array<OrderLineInput & { category: string | null }>,
): { units: number; earned: number; toNext: number } | null => {
    if (scheme.benefitType !== 'FREE_QTY' || scheme.slabBasis !== 'QTY') return null;
    const { categories = [], productIds = [] } = scheme.rule.productScope ?? {};
    const open = categories.length === 0 && productIds.length === 0;
    const qualifying = lines
        .filter((l) => open || productIds.includes(l.productId) || (!!l.category && categories.includes(l.category)))
        .reduce((s, l) => s + l.quantity, 0);
    const freeAt = (n: number) => {
        const slab = [...scheme.rule.slabs].sort((a, b) => b.min - a.min).find((s) => s.min <= n);
        return slab?.buy ? Math.floor(n / slab.buy) * (slab.get ?? 0) : 0;
    };
    const earned = freeAt(qualifying);
    let next = qualifying + 1;
    const ceiling = qualifying + Math.max(...scheme.rule.slabs.map((s) => Math.max(s.min, s.buy ?? 0))) + 1;
    while (next <= ceiling && freeAt(next) <= earned) next += 1;
    return { units: qualifying, earned, toNext: next <= ceiling ? next - qualifying : 0 };
};

/* Products by category and the brand's product details (Size, Colour…): the Product filter on B2B grids, and short labels. */

type ProductAttributes = { name: string; category: string | null; details: ProductDetail[] };

const UNCATEGORISED = 'Uncategorized';
/** How a product with no value for a detail shows in the filter. */
const NOT_SET = '(not set)';
const detailKey = (label: string) => `detail:${label}`;

/** Garment sizes smallest first; anything else sorts after them, alphabetically. */
const SIZE_ORDER = ['3XS', '2XS', 'XS', 'S', 'S-M', 'M', 'M-L', 'L', 'L-XL', 'XL', '2XL', '3XL', '4XL', '5XL'];
export const compareSizes = (a: string | null, b: string | null): number => {
    const rank = (s: string | null) => (s && SIZE_ORDER.includes(s) ? SIZE_ORDER.indexOf(s) : SIZE_ORDER.length);
    return rank(a) - rank(b) || (a ?? '').localeCompare(b ?? '');
};

/** The value a product shows for one filter key: its category, or one of its details. */
const filterValueOf = (product: ProductAttributes, key: string): string | undefined => {
    if (key === 'category') return product.category ?? UNCATEGORISED;
    if (key.startsWith('detail:')) return product.details.find((d) => detailKey(d.label) === key)?.value ?? NOT_SET;
    return undefined;
};

/** The Product filter: Category, then one dimension per product detail the brand has set (e.g. Size, Colour). */
export const productFilterParams = (products: ProductAttributes[]): FilterParam[] => {
    const labels = [...new Set(products.flatMap((p) => p.details.map((d) => d.label)))];
    return [{ key: 'category', label: 'Category' }, ...labels.map((label) => ({ key: detailKey(label), label }))].map(({ key, label }) => ({
        key,
        label,
        mode: 'multi' as const,
        options: [...new Set(products.map((p) => filterValueOf(p, key) as string))].sort(compareSizes).map((v) => ({ label: v, value: v })),
    }));
};

/** True when a product passes the Category / detail selections (an empty selection passes everything; other keys are ignored). */
export const matchesProductFilter = (product: ProductAttributes, filter: FilterValue): boolean =>
    Object.entries(filter).every(([key, selected]) => {
        const value = filterValueOf(product, key);
        return value === undefined || !selected?.length || selected.includes(value);
    });

/** "XL · Black" from the product's details, in the brand's order; the product name when it has none. */
export const variantLabel = (product: ProductAttributes): string => product.details.map((d) => d.value).filter(Boolean).join(' · ') || product.name;

/** Units and value of a set of order lines. */
export const sumLines = (lines: Array<{ quantity: number; lineTotal: number }>) => ({
    units: lines.reduce((s, l) => s + l.quantity, 0),
    value: Math.round(lines.reduce((s, l) => s + l.lineTotal, 0) * 100) / 100,
});

/** A saved order's lines as the totals read them: paid lines and FREE-n lines apart. */
export const splitSavedLines = (items: Array<{ lineItemId: string; quantity: number | string; lineTotal: number | string | null }>) => {
    const rows = items.map((i) => ({ free: i.lineItemId.startsWith('FREE-'), quantity: Number(i.quantity) || 0, lineTotal: Number(i.lineTotal) || 0 }));
    return { lines: rows.filter((r) => !r.free), freeLines: rows.filter((r) => r.free) };
};

/** A product detail row being edited in B2B settings: the source may still be unchosen. */
export interface ProductDetailDraft {
    label: string;
    source: ProductDetailSetting['source'] | null;
}

/** The rows that can be saved: a column name and a source each. */
export const savableDetails = (rows: ProductDetailDraft[]): ProductDetailSetting[] =>
    rows.filter((r): r is ProductDetailSetting => !!r.label.trim() && !!r.source).map((r) => ({ label: r.label.trim(), source: r.source }));
